import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import * as z from 'zod';

import { parseSchema } from '@repo/utils/parse-schema';

// GET /dashboard/overview (docs/overview-dashboard-api.md). Null is no good reading.
const value = z.number().nullable();

// t at the end of each of the 15 newest closed MRV minutes.
const pointSchema = z.object({ at: z.string(), t: z.number() });

// A flow's MRV figure as a counter: t since `since` (UTC start of the year) at each point, and the same count at the
// start of the UTC day, month and year of the newest point. Today is a point's t minus todayFromT.
const counterSchema = z.object({
  since: z.string(),
  points: z.array(pointSchema),
  todayFromT: z.number(),
  monthFromT: z.number(),
  yearFromT: z.number(),
});

const flowSchema = z.object({
  rateMtPerHour: value,
  // The top of the dial from the Øygarden terminal setup (Q3: Jetty 1's max rate, Q4: max injection rate).
  maxMtPerHour: value,
  // Q4: the min injection rate from the setup; the dial marks the range below it.
  minMtPerHour: value,
  // Null without MRV data.
  counter: counterSchema.nullable(),
});

export const overviewDashboardSchema = z.object({
  asOf: z.string(),
  // Now, the ship at port at Øygarden (null when none), and one value per minute over 24 h from `from` (kW).
  shorePower: z.object({
    kw: value,
    vessel: z.string().nullable(),
    from: z.string(),
    last24h: z.array(value),
  }),
  q3Liquid: flowSchema,
  q3Vapour: flowSchema,
  q4Liquid: flowSchema,
  // The pipeline inlet runs in reverse (Q4 is then zero); null without a valve reading.
  q4ReverseFlow: z.boolean().nullable(),
  tankFarm: z.object({
    volumeM3: value,
    pressureBar: value,
    temperatureC: value,
    tanks: z.array(z.object({ name: z.string(), levelPct: value })),
  }),
  wells: z.array(z.object({
    name: z.string(),
    chokeOpeningPct: value,
    injectionRateMtPerHour: value,
    wellheadTemperatureC: value,
    wellheadPressureBar: value,
    downholeTemperatureC: value,
    downholePressureBar: value,
  })),
  vessels: z.array(z.object({
    name: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    headingDegrees: value,
    speedKnots: value,
    positionAt: z.string(),
    // At the port, or en route to it; null without a stop near now. The time is the ETD at port, else the ETA.
    status: z.enum(['AtPort', 'EnRoute']).nullable(),
    port: z.string().nullable(),
    portTime: z.string().nullable(),
    // After the ship's last operation.
    onBoardMt: value,
    trail: z.array(z.object({ positionAt: z.string(), latitude: z.number(), longitude: z.number() })),
  })),
  series: z.object({
    from: z.string(),
    q3Liquid: z.array(value),
    q3Vapour: z.array(value),
    q4: z.array(value),
    q5A7: z.array(value),
    q5C1: z.array(value),
  }),
  // What is stored: the Q4 counter, t injected since 2025-08-21 09:44 UTC. Empty without MRV data.
  stored: z.array(pointSchema),
});

export type OverviewDashboard = z.infer<typeof overviewDashboardSchema>;
export type OverviewFlow = z.infer<typeof flowSchema>;
export type OverviewCounterPoint = z.infer<typeof pointSchema>;

// Each app fetches with its own auth. Every minute, also in a background tab: a wall screen or a second tab
// shows data at most a minute old (the server snapshot is at most 30 s old on top).
export function overviewDashboardQuery(fetchJson: () => Promise<unknown>) {
  return queryOptions({
    queryKey: ['overview-dashboard'],
    queryFn: async () => parseSchema(overviewDashboardSchema, await fetchJson()),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
